import { Phone, Users, School, Building2, CheckCircle, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import { breadcrumbSchema, seoKeywords } from '../utils/seoData';

const Workshops = () => {
  const programs = [
    {
      icon: School,
      title: 'School Programs',
      description: 'Interactive sessions for students to learn about snake identification, safety, and conservation.',
      duration: '1-2 hours',
      audience: 'Students (Grade 5-12)',
    },
    {
      icon: Users,
      title: 'Community Workshops',
      description: 'Educational programs for residential communities and apartment complexes.',
      duration: '2-3 hours',
      audience: 'Residents & Management',
    },
    {
      icon: Building2,
      title: 'Corporate Training',
      description: 'Snake safety awareness for corporate campuses and industrial areas.',
      duration: '1-2 hours',
      audience: 'Employees & Security Staff',
    },
  ];

  const topics = [
    'Snake identification - venomous vs non-venomous',
    'What to do if you encounter a snake',
    'Snake behavior and habitat',
    'First aid for snake bites',
    'Prevention methods for homes and offices',
    'Wildlife conservation awareness',
    'Common myths and facts about snakes',
    'Emergency contact procedures',
  ];

  const benefits = [
    'Reduce fear and panic around snakes',
    'Empower people with proper knowledge',
    'Prevent dangerous DIY snake handling attempts',
    'Build awareness about wildlife conservation',
    'Create safer communities',
    'Learn through visual presentations and case studies',
  ];

  const breadcrumbs = breadcrumbSchema([
    { name: 'Home', path: '/' },
    { name: 'Snake Awareness Workshops', path: '/workshops' }
  ]);

  return (
    <div className="min-h-screen">
      <SEO 
        title="Snake Awareness Workshops in Bangalore | Safety Training Programs"
        description="Professional snake awareness workshops and safety training in Bangalore for schools, apartments, and corporates. Call +91 8310755854 or +91 9845080903 to book."
        keywords={seoKeywords.workshops}
        canonical="/workshops"
        schemaData={breadcrumbs}
      />
      
      {/* Hero */}
      <section className="bg-gradient-to-b from-emerald-50 to-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <Award className="inline h-4 w-4 mr-1" />
              Educational Programs
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Snake Awareness Workshops in Bangalore
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Empowering communities with knowledge about snake safety, identification, and conservation through interactive educational programs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a href="tel:+918310755854">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <Phone className="mr-2 h-5 w-5" />
                  Alhan: +91 8310755854
                </Button>
              </a>
              <a href="tel:+919845080903">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <Phone className="mr-2 h-5 w-5" />
                  Mohan: +91 9845080903
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Programs */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Programs</h2>
              <p className="text-gray-600">Customized workshops for different audiences</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {programs.map((program, index) => {
                const Icon = program.icon;
                return (
                  <Card key={index} className="hover:shadow-lg transition-shadow border-emerald-100">
                    <CardContent className="p-6">
                      <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                        <Icon className="h-6 w-6 text-emerald-600" />
                      </div>
                      <h3 className="text-xl font-semibold mb-3 text-gray-900">{program.title}</h3>
                      <p className="text-gray-600 mb-4">{program.description}</p>
                      <div className="space-y-2 text-sm">
                        <p className="text-gray-700">
                          <span className="font-semibold">Duration:</span> {program.duration}
                        </p>
                        <p className="text-gray-700">
                          <span className="font-semibold">For:</span> {program.audience}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Topics Covered */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">What You'll Learn</h2>
                <p className="text-gray-600 mb-6">
                  Our comprehensive workshops cover everything you need to know about snake safety and conservation:
                </p>
                <ul className="space-y-3">
                  {topics.map((topic, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{topic}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Workshop Benefits</h2>
                <p className="text-gray-600 mb-6">
                  Investing in snake awareness education brings multiple benefits to your community:
                </p>
                <ul className="space-y-3">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 mt-6">
                  <h3 className="font-semibold text-emerald-900 mb-2">Interactive Learning</h3>
                  <p className="text-emerald-800 text-sm">
                    Our workshops include visual presentations, videos, and real-life case studies to provide comprehensive understanding of snake behavior and safety.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
              What People Say
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-emerald-100">
                <CardContent className="p-6">
                  <p className="text-gray-700 mb-4">
                    "Alhan and Mohan conducted an excellent workshop for our apartment residents. Everyone learned so much about snake safety. Highly recommended!"
                  </p>
                  <p className="font-semibold text-gray-900">- Resident Welfare Association</p>
                  <p className="text-sm text-gray-600">Marathahalli, Bangalore</p>
                </CardContent>
              </Card>
              <Card className="border-emerald-100">
                <CardContent className="p-6">
                  <p className="text-gray-700 mb-4">
                    "Our students were fascinated! The interactive session was both educational and engaging. Thank you for spreading awareness."
                  </p>
                  <p className="font-semibold text-gray-900">- School Principal</p>
                  <p className="text-sm text-gray-600">Whitefield, Bangalore</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-emerald-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Book a Workshop?
          </h2>
          <p className="text-xl mb-8 text-emerald-50">
            Contact us to schedule an educational program for your school, community, or organization
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a href="tel:+918310755854">
              <Button size="lg" className="bg-white text-emerald-600 hover:bg-gray-100 text-lg px-8 py-6">
                <Phone className="mr-2 h-5 w-5" />
                Alhan: +91 8310755854
              </Button>
            </a>
            <a href="tel:+919845080903">
              <Button size="lg" className="bg-white text-emerald-600 hover:bg-gray-100 text-lg px-8 py-6">
                <Phone className="mr-2 h-5 w-5" />
                Mohan: +91 9845080903
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Workshops;